import { FC, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, IconButton, Paper } from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import {
  DataGridPro as DataGrid,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLoadingOverlay,
  GridLogicOperator,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import moment from "moment";

import Pagination from "src/Components/DataGrid/Pagination";
import {
  dateOperators,
  numericOperator,
  statusesOptions,
  stringOperators,
} from "src/Pages/Company/SubscriptionBenefits/constans";
import { EventLogsSubscription } from "src/Pages/Company/SubscriptionBenefits/models";
import { useFetchSubscriptions } from "src/Pages/Company/SubscriptionBenefits/query";
import { parseDataGridFilters } from "src/Pages/Company/SubscriptionBenefits/utils";

import translateLabels from "src/utils/Translations/translateLabels";
import __ from "src/utils/Translations";
import { useGridStatePersistence } from "src/utils/hooks/absences/useGridStatePersistence";
import SubscriptionBenefitsPreview from "./SubscriptionBenefitsPreview";

interface Props {
  companyId: string;
  employeeId?: string | undefined;
}

const SubscriptionBenefitsTable: FC<Props> = ({ companyId, employeeId }) => {
  const apiRef = useGridApiRef();
  const { initialState } = useGridStatePersistence({
    storageKey: `event-logs-subscriptions-table${companyId || ""}${
      employeeId || ""
    }`,
    apiRef,
  });

  const stringFilterOperators = useMemo(
    () =>
      getGridStringOperators().filter((operator) =>
        stringOperators.includes(operator.value)
      ),
    []
  );
  const numericFilterOperators = useMemo(
    () =>
      getGridNumericOperators().filter((operator) =>
        numericOperator.includes(operator.value)
      ),
    []
  );
  const dateFilterOperators = useMemo(
    () =>
      getGridDateOperators().filter((operator) =>
        dateOperators.includes(operator.value)
      ),
    []
  );

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });

  const filters = useMemo(() => {
    const customFilters: GridFilterItem[] = [];
    if (companyId) {
      customFilters.push({
        field: "company.id",
        operator: "equals",
        value: companyId,
      });
    }
    if (employeeId) {
      customFilters.push({
        field: "employee.id",
        operator: "equals",
        value: employeeId,
      });
    }

    return parseDataGridFilters(customFilters.concat(filterModel?.items));
  }, [companyId, employeeId, filterModel?.items]);

  const { data, isFetching, isError, error } = useFetchSubscriptions(
    filters,
    paginationModel.page + 1,
    paginationModel.pageSize,
    sortModel?.map(({ field, sort }) => `${field}:${sort}`)
  );

  const rowCountRef = useRef(data?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (data?.totalElements !== undefined) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  const columns = useMemo(
    () =>
      (
        [
          {
            headerName: __("ID"),
            field: "employee.id",
            valueGetter: (_value, row) => row.employee?.id,
            type: "string",
            filterOperators: stringFilterOperators,
            renderCell: ({ row, value }) => (
              <>
                {row.employee && row.company && (
                  <IconButton
                    component={RouterLink}
                    to={`/company/edit/${row.company.id}/employee-management/employees/${row.employee.id}#subscription_benefits`}
                    title={__("Przejdź do karty pracownika")}
                    size="small"
                  >
                    <BadgeIcon />
                  </IconButton>
                )}
                {value}
              </>
            ),
            width: 350,
          },
          {
            headerName: __("Imię"),
            field: "employee.firstName",
            valueGetter: (_value, row) => row.employee?.firstName,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("Nazwisko"),
            field: "employee.lastName",
            valueGetter: (_value, row) => row.employee?.lastName,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("E-mail służbowy"),
            field: "employee.businessEmail",
            valueGetter: (_value, row) => row.employee?.businessEmail,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("E-mail prywatny"),
            field: "employee.privateEmail",
            valueGetter: (_value, row) =>
              row.employee?.privateEmail?.join(", "),
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("Skrócona nazwa firmy"),
            field: "company.shortName",
            valueGetter: (_value, row) => row.company.shortName,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Id firmy"),
            field: "company.id",
            valueGetter: (_value, row) => row.company.id,
            type: "string",
            filterOperators: stringFilterOperators,
            renderCell: (params) => (
              <>
                <IconButton
                  component={RouterLink}
                  to={`/company/edit/${params.row.company.id}/subscription-benefits`}
                  title={__("Przejdź do profilu firmy")}
                  size="small"
                >
                  <CorporateFareIcon />
                </IconButton>
                {params.value}
              </>
            ),
          },
          {
            headerName: __("Numer pracownika"),
            field: "employee.fkNumber",
            valueGetter: (_value, row) => row.employee?.fkNumber,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("Identyfikator abonamentu"),
            field: "id",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 350,
          },
          {
            headerName: __("Status abonamentu"),
            field: "status",
            type: "singleSelect",
            valueOptions: translateLabels(statusesOptions),
            width: 150,
          },
          {
            headerName: __("Nazwa produktu"),
            field: "productName",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Data utworzenia"),
            field: "createdAt",
            type: "date",
            filterOperators: dateFilterOperators,
            valueGetter: (value) => value && new Date(value),
            renderCell: ({ value }) => (
              <span
                title={value && moment(value).format("YYYY-MM-DD HH:mm:ss")}
              >
                {value && moment(value).format("YYYY-MM-DD")}
              </span>
            ),
            width: 150,
          },
          {
            headerName: __("Data anulowania"),
            field: "canceledAt",
            type: "date",
            filterOperators: dateFilterOperators,
            valueGetter: (value) => value && new Date(value),
            renderCell: ({ value }) => (
              <span
                title={value && moment(value).format("YYYY-MM-DD HH:mm:ss")}
              >
                {value && moment(value).format("YYYY-MM-DD")}
              </span>
            ),
            width: 150,
          },
          {
            headerName: __("Data startu"),
            field: "startDate",
            type: "date",
            filterOperators: dateFilterOperators,
            valueGetter: (value) => value && new Date(value),
            valueFormatter: (value) =>
              value && new Date(value).toISOString().slice(0, 10),
            width: 150,
          },
          {
            headerName: __("Data rezygnacji"),
            field: "resignationDate",
            type: "date",
            filterOperators: dateFilterOperators,
            valueGetter: (value) => value && new Date(value),
            valueFormatter: (value) =>
              value && new Date(value).toISOString().slice(0, 10),
            width: 150,
          },
          {
            headerName: __("Priorytet"),
            field: "priority",
            type: "number",
            filterOperators: numericFilterOperators,
            width: 100,
          },
          {
            headerName: __("Powód rezygnacji"),
            field: "resignationReason",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Powód anulowania"),
            field: "cancellationReason",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Akcja"),
            field: "actions",
            type: "actions",
            align: "right",
            width: 60,
            getActions: (params: GridRowParams) => [
              <SubscriptionBenefitsPreview subscriptionId={params.row.id} />,
            ],
          },
        ] as GridColDef<EventLogsSubscription>[]
      )
        .filter((col) => !employeeId || !col.field.includes("employee."))
        .filter((col) => !companyId || !col.field.includes("company.")),
    [
      companyId,
      dateFilterOperators,
      employeeId,
      numericFilterOperators,
      stringFilterOperators,
    ]
  );

  if (!initialState) {
    return null;
  }

  return (
    <Paper
      sx={{
        padding: "1rem",
      }}
    >
      {isError && (
        <Alert variant="outlined" color="warning">
          {String(error)}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 400,
        }}
      >
        <DataGrid
          apiRef={apiRef}
          columns={columns}
          getRowId={(row) => row.id}
          rows={data?.content || []}
          rowCount={rowCount}
          loading={isFetching}
          initialState={initialState}
          pinnedColumns={{ left: [], right: ["actions"] }}
          disableRowSelectionOnClick
          disableVirtualization
          disableEval
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          showToolbar
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: false,
            },
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "circular-progress",
            },
            basePagination: {
              material: {
                ActionsComponent: Pagination,
              },
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default SubscriptionBenefitsTable;
